import { lazy, Suspense } from 'react';
import {
  Outlet,
  Navigate,
  RouterProvider,
  createBrowserRouter,
} from 'react-router-dom';
import { Protect } from '../middleware/protect';

import DashboardLayout from '../layouts/dashboard';
import { AuthProvider } from '../../hooks/useAuth';
import { PaymentDetailsDTO } from '../../openapi/models/PaymentDetailsDTO';
import { ResponseDTO } from '../../openapi/models/ResponseDTO';
import { mockPaymentDetails } from '../../_mock/payment';
import { ApiError, InvoicesService, PaymentsService } from '../../openapi';
import { PointsService } from '../../openapi/services/PointsService';

export const RootPage = lazy(() => import('../pages/root'));
export const SignInPage = lazy(() => import('../pages/signIn'));
export const PaymentsPage = lazy(() => import('../pages/payments'));
export const PaymentPage = lazy(() => import('../pages/payment'));
export const PaymentErrorPage = lazy(() => import('../pages/payment/error'));
export const InvoicesPage = lazy(() => import('../pages/invoices'));
export const PointsPage = lazy(() => import('../pages/points'));
export const PointPage = lazy(() => import('../pages/point'));
export const CreareInvoicePage = lazy(() => import('../pages/invoices.create'));
export const PaymentRefundPage = lazy(() => import('../pages/payment.refund'));
export const RefundsPage = lazy(() => import('../pages/refunds'));
export const Page404 = lazy(() => import('../pages/404'));

export default function Router() {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).setHours(
    0,
    0,
    0,
    0,
  );
  // const nowDay = new Date(
  //   now.getFullYear(),
  //   now.getMonth(),
  //   now.getDate(),
  //   now.getHours(),
  //   no
  // );//.setHours(23, 59, 59, 999);

  const router = createBrowserRouter([
    {
      element: (
        <AuthProvider>
          <Protect>
            <DashboardLayout>
              <Suspense>
                <Outlet />
              </Suspense>
            </DashboardLayout>
          </Protect>
        </AuthProvider>
      ),
      children: [
        {
          element: <RootPage />,
          index: true,
        },
        {
          path: 'points',
          children: [
            {
              index: true,
              loader: async () => {
                return PointsService.getPoints().catch((e: ApiError) => {
                  if (e.status === 401 || e.status === 498)
                    return <Navigate to="/signIn" />;
                });
              },
              element: <PointsPage />,
              errorElement: <PaymentErrorPage />,
            },
            {
              path: ':pointId',
              loader: async ({ params }) => {
                return PointsService.getPointDetails(params.pointId)
                  .then((data) => {
                    return {
                      ...data,
                      pointId: params.pointId,
                    };
                  })
                  .catch((e: ApiError) => {
                    if (e.status === 401 || e.status === 498)
                      return <Navigate to="/signIn" />;
                  });
              },
              element: <PointPage />,
            },
          ],
        },
        {
          path: 'payments',
          children: [
            { index: true, element: <PaymentsPage /> },
            {
              path: ':paymentId',
              loader: async ({ params }) => {
                if (params.paymentId === '-1') {
                  return {
                    status: 'success',
                    data: mockPaymentDetails,
                  } as ResponseDTO<PaymentDetailsDTO>;
                } else {
                  return PaymentsService.getPaymentDetails(
                    params.paymentId,
                  ).catch((e: ApiError) => {
                    if (e.status === 401 || e.status === 498)
                      return <Navigate to="/signIn" />;
                  });
                }
              },
              element: <PaymentPage />,
              errorElement: <PaymentErrorPage />,
            },
            {
              path: ':paymentId/refund',
              loader: async ({ params }) => {
                if (params.paymentId === '-1') {
                  return {
                    status: 'success',
                    data: mockPaymentDetails,
                  } as ResponseDTO<PaymentDetailsDTO>;
                } else {
                  return PaymentsService.getPaymentDetails(
                    params.paymentId,
                  ).catch((e: ApiError) => {
                    if (e.status === 401 || e.status === 498)
                      return <Navigate to="/signIn" />;
                  });
                }
              },
              element: <PaymentRefundPage />,
              errorElement: <PaymentErrorPage />,
            },
          ],
        },
        {
          path: 'invoices',
          children: [
            {
              index: true,
              // path: 'list',
              element: <InvoicesPage />,
            },

            {
              path: 'create',
              loader: async () => {
                return PointsService.getPoints().catch((e: ApiError) => {
                  if (e.status === 401 || e.status === 498)
                    return <Navigate to="/signIn" />;
                });
              },
              element: <CreareInvoicePage />,
              errorElement: <PaymentErrorPage />,
            },
          ],
        },

        { path: 'refunds', element: <RefundsPage /> },
      ],
    },
    {
      path: 'signIn',
      element: (
        <AuthProvider>
          <SignInPage />
        </AuthProvider>
      ),
    },
    {
      path: '404',
      element: <Page404 />,
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);

  return <RouterProvider router={router} />;
}
